/**
 * Analyzer: Performance Decay (First Half vs Second Half)
 *
 * Detects the classic "started strong, gave it back" pattern vs the
 * opposite "recovered from early losses" pattern. Either is useful
 * because the former flags edge-fade / overstay, the latter tells
 * you the first-trade read was wrong but adjustment worked.
 *
 * Requires >= 4 trades in the session to avoid noise.
 */

import { CATEGORY, SEVERITY, makeObservation } from '../types';
import { sortByEntryTime, fmt$ } from '../utils';

export const key = 'performanceDecay';

export const analyze = (ctx) => {
  const { session, enriched } = ctx;
  if (!session || !Array.isArray(enriched) || enriched.length < 4) return null;

  const sorted = sortByEntryTime(enriched);
  const half = Math.floor(sorted.length / 2);
  const first = sorted.slice(0, half);
  const second = sorted.slice(half);
  const firstPnl = first.reduce((s, x) => s + x.netPnl, 0);
  const secondPnl = second.reduce((s, x) => s + x.netPnl, 0);

  // Decay: started green, faded red (second half loss > 50% of first-half gain)
  if (firstPnl > 0 && secondPnl < 0 && Math.abs(secondPnl) > Math.abs(firstPnl) * 0.5) {
    return makeObservation({
      id: `${key}:${session.id}:decay`,
      analyzer: key,
      scope: 'session',
      scopeId: session.id,
      severity: SEVERITY.WARNING,
      category: CATEGORY.DISCIPLINE,
      title: `Started strong, gave it back`,
      detail: `First half ${fmt$(firstPnl)} → second half ${fmt$(secondPnl)}. Classic edge-fade or overstay after the window closed.`,
      action: 'Define a stop-trading rule (time or $ target) so winning sessions don\'t roundtrip.',
      evidence: { firstPnl, secondPnl, firstCount: first.length, secondCount: second.length },
      metric: { label: 'Give-back', value: secondPnl, baseline: firstPnl, unit: '$' },
      confidence: 0.82,
      priority: 78
    });
  }

  // Recovery: red first half, green second half (second-half gain covers losses)
  if (firstPnl < 0 && secondPnl > Math.abs(firstPnl)) {
    return makeObservation({
      id: `${key}:${session.id}:recover`,
      analyzer: key,
      scope: 'session',
      scopeId: session.id,
      severity: SEVERITY.SUCCESS,
      category: CATEGORY.DISCIPLINE,
      title: `Recovered well from a red start`,
      detail: `First half ${fmt$(firstPnl)} → second half ${fmt$(secondPnl)}. Adjustment worked.`,
      action: 'Capture what you changed between halves — that\'s the reusable insight.',
      evidence: { firstPnl, secondPnl, firstCount: first.length, secondCount: second.length },
      metric: { label: 'Recovery', value: secondPnl, baseline: firstPnl, unit: '$' },
      confidence: 0.82,
      priority: 60
    });
  }

  return null;
};
